import { flow, Instance, types } from 'mobx-state-tree';
import { logError } from 'src/_shared/logger';
import { extractUserIdFromAccessToken } from 'src/_shared/utils/auth/extractUserIdFromAccessToken';
import { getUserIsRetailer } from 'src/_shared/utils/auth/getUserIsRetailer';
import { BaseListenersModel } from '../_models/_base/baseListenersModel';
import { sdkProjects } from '../_shared/sdkUtils';
import { IProject } from '@photosi/expo-photosi-sdk/build/Project';
import { updateTrackingServicesWith } from 'src/_shared/utils/updateTrackingServicesWith';

export enum UserTypeEnum {
  USER_TYPE_RETAILER = 'retailer',
  USER_TYPE_CONSUMER = 'consumer',
}

export type TUser = typeof User;
export type UserInstance = Instance<TUser>;
export interface IUser extends UserInstance {}

export const User = BaseListenersModel.named('User')
  .props({
    id: types.maybeNull(types.string),
    isLogged: types.boolean,
    userType: types.enumeration(Object.values(UserTypeEnum)),
    accessToken: types.string,
  })
  .views(self => ({
    get sdkProject(): IProject | undefined {
      // User Store is the only one who is created before project initialization
      if (self.root?.project?.activeProjectId) {
        return sdkProjects.get(self.root.project.activeProjectId);
      }
      return undefined;
    },
    get isRetailer() {
      // Retail is a B2B user (logged with PhotoSì PRO)
      return self.userType === UserTypeEnum.USER_TYPE_RETAILER;
    },
    get isConsumer() {
      // Consumer is a B2C user (logged with PhotoSì logo)
      return self.userType === UserTypeEnum.USER_TYPE_CONSUMER;
    },
  }))
  .actions(self => {
    const setAccessToken = (accessToken: string) => {
      self.accessToken = accessToken;
    };

    const setIsLogged = (isLogged: boolean) => {
      self.isLogged = isLogged;
    };

    const setUserType = (userType: UserTypeEnum) => {
      self.userType = userType;
    };

    const updateUser = flow(function* updateUser({
      accessToken,
      id,
      isLogged,
      userType,
    }: {
      accessToken: string;
      id: string;
      isLogged: boolean;
      userType: UserTypeEnum;
    }) {
      self.accessToken = accessToken;
      self.id = id;
      self.isLogged = isLogged;
      self.userType = userType;

      yield updateTrackingServicesWith({ id, userType });
    });

    return {
      setAccessToken,
      setIsLogged,
      setUserType,
      updateUser,
    };
  })
  .actions(self => {
    const login = flow(function* login({ accessToken }: { accessToken: string }) {
      let hasLoggedInSuccessfully = true;
      try {
        const isRetailer = yield getUserIsRetailer();
        self.updateUser({
          id: extractUserIdFromAccessToken(accessToken),
          accessToken,
          isLogged: true,
          userType: isRetailer ? UserTypeEnum.USER_TYPE_RETAILER : UserTypeEnum.USER_TYPE_CONSUMER,
        });

        if (self.sdkProject) {
          // reconcile user account with the user access token and remove guest token
          yield self.sdkProject.reconcile(accessToken);
        }
      } catch (error) {
        logError('[User] login error', error);
        hasLoggedInSuccessfully = false;
      }

      return hasLoggedInSuccessfully;
    });

    return {
      login,
    };
  })
  .actions(self => {
    const afterCreate = flow(function* afterCreate() {
      yield updateTrackingServicesWith({ id: self.id, userType: self.userType });
    });

    return {
      afterCreate,
    };
  });
